// Courriers et recommandés suivis (F-061, D-18, SRC-19) : modèle, relecture, validation, envoi par le canal adapté,
// preuves du prestataire (simulé) ou suivi manuel du recommandé postal.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { get, post, put } from "@core/api";
import { useGet } from "@core/hooks";
import { dateTime, money, today } from "@core/format";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, Input, Loading, Modal, Notice, PageHeader, Select, SimBadge, Textarea, fieldError } from "@core/ui";
import { DocLink, LeaseSelect, useAction, useG, useLeases } from "./shared";

type Letter = {
  id: string;
  lease_id: string | null;
  template: string;
  subject: string;
  body: string;
  recipient_name: string;
  recipient_address: string;
  recipient_email: string;
  channel: string;
  status: string;
  status_label: string;
  channel_label: string;
  recipient_consent: boolean;
  recipient_consent_note: string;
  document_id: string | null;
  provider_ref: string | null;
  provider_mode: string | null;
  cost_cents: number | null;
  sent_at: string | null;
  created_at: string;
};
type ListData = { items: Letter[]; channels: { value: string; label: string }[]; templates: { value: string; label: string }[]; registered: { provider: string; mode: string; price_cents: number; notice: string } };

const TONE: Record<string, "neutre" | "encre" | "succes" | "attention" | "erreur"> = {
  brouillon: "neutre",
  valide: "encre",
  a_imprimer: "attention",
  envoye: "succes",
  depose: "encre",
  presente: "encre",
  accuse_reception: "succes",
  refuse: "erreur",
  non_reclame: "attention",
  echec: "erreur",
  annule: "neutre",
};

export function CourriersPage() {
  const { g, can } = useG();
  const [params, setParams] = useSearchParams();
  const selected = params.get("courrier");
  const q = useGet<ListData>(g("/letters"));
  const [creating, setCreating] = useState(false);
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title="Courriers et recommandés"
        intro="Préparez un courrier à partir d’un modèle, relisez-le, validez-le puis choisissez le canal. Courriel, recommandé électronique et courrier postal n’ont pas la même portée pour tous les actes."
        actions={can("operate") ? <Button onClick={() => setCreating(true)}>Nouveau courrier</Button> : null}
      />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <>
          <Notice tone="simulateur" title="Lettre recommandée électronique : simulateur">
            {q.data.registered.notice} Aucun prestataire n’est encore contractualisé (AR24 est un candidat documenté) : dépôts et preuves sont simulés.
          </Notice>
          <Card title="Courriers">
            {q.data.items.length === 0 ? <Empty title="Aucun courrier">Les relances, révisions et échéanciers préparent aussi des courriers ici.</Empty> : null}
            <ul className="gestion-list">
              {q.data.items.map((l) => (
                <li key={l.id} className="gestion-line">
                  <span>
                    <strong>{l.subject}</strong> · {l.recipient_name}
                    <span className="block small muted">
                      {l.channel_label} · créé le {dateTime(l.created_at)}
                      {l.sent_at ? ` · envoyé le ${dateTime(l.sent_at)}` : ""}
                    </span>
                  </span>
                  <span className="gestion-actions">
                    <Badge tone={TONE[l.status] ?? "neutre"}>{l.status_label}</Badge>
                    <Button variant="discret" small onClick={() => setParams({ courrier: l.id })}>
                      Ouvrir
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
          {creating ? <NewLetter data={q.data} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setParams({ courrier: id }); }} /> : null}
          {selected ? <LetterModal id={selected} data={q.data} onClose={() => setParams({})} /> : null}
        </>
      ) : null}
    </div>
  );
}

function NewLetter({ data, onClose, onCreated }: { data: ListData; onClose: () => void; onCreated: (id: string) => void }) {
  const { g } = useG();
  const leases = useLeases();
  const { busy, run, error } = useAction();
  const [f, setF] = useState({ lease_id: "", template: "libre", subject: "", body: "", recipient_name: "", recipient_address: "", recipient_email: "", channel: "email" });
  useEffect(() => {
    let live = true;
    get<any>(g(`/letters/template?template=${f.template}${f.lease_id ? `&lease_id=${f.lease_id}` : ""}`))
      .then((t) => live && setF((x) => ({ ...x, subject: t.subject || x.subject, body: t.body, recipient_name: t.recipient_name || x.recipient_name, recipient_address: t.recipient_address || x.recipient_address, recipient_email: t.recipient_email || x.recipient_email })))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [f.template, f.lease_id]);
  return (
    <Modal
      open
      onClose={onClose}
      title="Nouveau courrier"
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Annuler
          </Button>
          <Button
            busy={busy === "create"}
            onClick={async () => {
              const r = await run("create", () => post<any>(g("/letters"), { ...f, lease_id: f.lease_id || null }), "Brouillon enregistré : relisez-le puis validez-le.");
              if (r) onCreated(r.id);
            }}
          >
            Enregistrer le brouillon
          </Button>
        </>
      }
    >
      <div className="form-row">
        <LeaseSelect leases={leases.data?.items ?? []} value={f.lease_id} onChange={(v) => setF({ ...f, lease_id: v })} label="Bail (facultatif)" />
        <Select label="Modèle" value={f.template} onChange={(e) => setF({ ...f, template: e.target.value })} options={data.templates} />
      </div>
      <div className="form-row">
        <Input label="Destinataire" value={f.recipient_name} onChange={(e) => setF({ ...f, recipient_name: e.target.value })} error={fieldError(error, "recipient_name")} />
        <Select label="Canal" value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} options={data.channels} />
      </div>
      <div className="form-row">
        <Input label="Adresse électronique" type="email" value={f.recipient_email} onChange={(e) => setF({ ...f, recipient_email: e.target.value })} error={fieldError(error, "recipient_email")} />
        <Input label="Adresse postale" value={f.recipient_address} onChange={(e) => setF({ ...f, recipient_address: e.target.value })} error={fieldError(error, "recipient_address")} />
      </div>
      <Input label="Objet" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} error={fieldError(error, "subject")} />
      <Textarea label="Texte" rows={14} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} error={fieldError(error, "body")} help="Remplacez les éléments entre crochets. Ton factuel et respectueux." />
    </Modal>
  );
}

function LetterModal({ id, data, onClose }: { id: string; data: ListData; onClose: () => void }) {
  const { g, can } = useG();
  const q = useGet<Letter & { events: any[] }>(g(`/letters/${id}`));
  const { busy, run, error } = useAction();
  const [consent, setConsent] = useState({ ok: false, note: "" });
  const [edit, setEdit] = useState<Letter | null>(null);
  const [manual, setManual] = useState({ status: "depose", occurred_on: today(), detail: "" });
  const l = q.data;
  const postal = l?.channel === "courrier" || l?.channel === "recommande_postal";
  const nextManual = l?.channel === "courrier" ? [{ value: "envoye", label: "Posté" }] : l?.status === "a_imprimer" ? [{ value: "depose", label: "Déposé à la poste" }] : l?.status === "depose" ? [{ value: "presente", label: "Présenté (avis de passage)" }, { value: "accuse_reception", label: "Avis de réception signé" }, { value: "non_reclame", label: "Non réclamé (retour)" }, { value: "echec", label: "Non distribué" }] : l?.status === "presente" ? [{ value: "accuse_reception", label: "Avis de réception signé" }, { value: "refuse", label: "Refusé" }, { value: "non_reclame", label: "Non réclamé" }] : [];
  return (
    <Modal open onClose={onClose} title={l ? l.subject : "Courrier"}>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {l ? (
        <div className="stack">
          <p>
            <Badge tone={TONE[l.status] ?? "neutre"}>{l.status_label}</Badge> {l.channel_label}
            {l.provider_mode ? <SimBadge mode={l.provider_mode} /> : null}
          </p>
          <p className="small">
            À : {l.recipient_name}
            {l.recipient_email ? ` · ${l.recipient_email}` : ""}
            {l.recipient_address ? ` · ${l.recipient_address}` : ""}
          </p>
          {edit ? (
            <div className="stack-sm">
              <Input label="Objet" value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} />
              <Select label="Canal" value={edit.channel} onChange={(e) => setEdit({ ...edit, channel: e.target.value })} options={data.channels} />
              <Input label="Adresse électronique" value={edit.recipient_email} onChange={(e) => setEdit({ ...edit, recipient_email: e.target.value })} />
              <Input label="Adresse postale" value={edit.recipient_address} onChange={(e) => setEdit({ ...edit, recipient_address: e.target.value })} />
              <Textarea label="Texte" rows={14} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
              <div className="gestion-actions">
                <Button busy={busy === "save"} onClick={async () => { const ok = await run("save", () => put(g(`/letters/${l.id}`), { ...edit, lease_id: edit.lease_id }), "Modifications enregistrées ; validez à nouveau le courrier."); if (ok) setEdit(null); }}>
                  Enregistrer
                </Button>
                <Button variant="discret" onClick={() => setEdit(null)}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <div className="gestion-letter-body">{l.body}</div>
          )}
          <DocLink id={l.document_id}>Courrier validé (PDF)</DocLink>
          {can("operate") && !edit ? (
            <>
              {l.status === "brouillon" ? (
                <div className="gestion-actions">
                  <Button busy={busy === "validate"} onClick={() => run("validate", () => post(g(`/letters/${l.id}/validate`)), "Courrier validé : le PDF définitif est prêt.")}>
                    Valider le courrier
                  </Button>
                  <Button variant="discret" onClick={() => setEdit(l)}>
                    Modifier
                  </Button>
                  <Button variant="discret" busy={busy === "cancel"} onClick={() => run("cancel", () => post(g(`/letters/${l.id}/cancel`)), "Courrier annulé.")}>
                    Annuler le courrier
                  </Button>
                </div>
              ) : null}
              {l.status === "valide" ? (
                <Card title="Envoyer">
                  {l.channel === "recommande_electronique" ? (
                    <>
                      <Notice>{data.registered.notice}</Notice>
                      <p className="small">Coût indicatif du prestataire (simulé) : {money(data.registered.price_cents)} par envoi.</p>
                      <Checkbox label="Le destinataire a accepté de recevoir des lettres recommandées électroniques" checked={consent.ok} onChange={(v) => setConsent({ ...consent, ok: v })} />
                      <Input label="Comment cet accord a-t-il été recueilli ?" value={consent.note} onChange={(e) => setConsent({ ...consent, note: e.target.value })} error={fieldError(error, "recipient_consent_note")} />
                    </>
                  ) : null}
                  {postal ? <p className="small">Le courrier sera à imprimer et à déposer ; vous enregistrerez ensuite le dépôt et l’avis de réception (tarif postal à votre charge).</p> : null}
                  <div className="gestion-actions">
                    <Button busy={busy === "send"} onClick={() => run("send", () => post<any>(g(`/letters/${l.id}/send`), { recipient_consent: consent.ok, recipient_consent_note: consent.note }), (r: any) => (r.status === "depose" ? `Déposé chez le prestataire (${r.mode}).` : r.status === "a_imprimer" ? "Courrier prêt à imprimer." : "Courriel envoyé (simulé)."))}>
                      Envoyer par ce canal
                    </Button>
                    <Button variant="discret" onClick={() => setEdit(l)}>
                      Modifier
                    </Button>
                  </div>
                </Card>
              ) : null}
              {postal && nextManual.length ? (
                <Card title="Suivi de l’envoi postal">
                  <div className="form-row">
                    <Select label="Étape" value={nextManual.some((x) => x.value === manual.status) ? manual.status : nextManual[0].value} onChange={(e) => setManual({ ...manual, status: e.target.value })} options={nextManual} />
                    <Input label="Date" type="date" max={today()} value={manual.occurred_on} onChange={(e) => setManual({ ...manual, occurred_on: e.target.value })} />
                    <Input label="Précision" value={manual.detail} onChange={(e) => setManual({ ...manual, detail: e.target.value })} />
                  </div>
                  <Button
                    variant="secondaire"
                    busy={busy === "manual"}
                    onClick={() => run("manual", () => post(g(`/letters/${l.id}/events`), { ...manual, status: nextManual.some((x) => x.value === manual.status) ? manual.status : nextManual[0].value }), "Étape enregistrée.")}
                  >
                    Enregistrer l’étape
                  </Button>
                </Card>
              ) : null}
              {l.channel === "recommande_electronique" && l.provider_mode === "simulateur" && (l.status === "depose" || l.status === "presente") ? (
                <Card title="Outils du simulateur">
                  <div className="gestion-actions">
                    {(l.status === "depose" ? ["presente", "accuse_reception", "non_reclame", "echec"] : ["accuse_reception", "refuse", "non_reclame"]).map((s) => (
                      <Button key={s} variant="secondaire" small busy={busy === s} onClick={() => run(s, () => post(g(`/letters/${l.id}/simulate`), { status: s }), "Événement simulé reçu avec sa preuve.")}>
                        {s === "presente" ? "Présenté" : s === "accuse_reception" ? "Accusé de réception" : s === "refuse" ? "Refusé" : s === "non_reclame" ? "Non réclamé" : "Échec"}
                      </Button>
                    ))}
                  </div>
                </Card>
              ) : null}
            </>
          ) : null}
          <Card title="Suivi et preuves">
            {l.events.length === 0 ? <p className="muted">Aucun événement.</p> : null}
            <ul className="gestion-list">
              {l.events.map((e: any) => (
                <li key={e.id} className="gestion-line">
                  <span>
                    <strong>{e.status_label}</strong> · {dateTime(e.occurred_at)}
                    <span className="block small muted">
                      {e.detail} ({e.source})
                    </span>
                  </span>
                  <DocLink id={e.proof_document_id}>Preuve</DocLink>
                </li>
              ))}
            </ul>
            {l.provider_ref ? <p className="small muted">Référence du prestataire : {l.provider_ref}</p> : null}
          </Card>
        </div>
      ) : null}
    </Modal>
  );
}
